/**
 * Rubrique « Audios animations » (permission `speaker`, migrations 0678-0679) :
 * un compte (de n'importe quel rôle) à qui l'admin a donné la permission prête
 * sa voix aux animations SKILLCARDS.
 *
 *   - liste : RPC `get_speaker_voice_tasks()` — animations dont des textes n'ont
 *     pas encore de voix, groupées ici par matière puis niveau (`groupSpeakerTasks`) ;
 *   - une animation : edge fn `animation-speaker` (`animation`) — brouillon
 *     validé (pour la tester) et textes à dire ;
 *   - une prise : transcrite et déposée par `animation-voice`, puis gardée par
 *     `animation-speaker` (`keep`) dans le brouillon. Les élèves l'entendent à
 *     la prochaine publication par l'admin.
 *   - admin : donner / retirer la permission (`user_permissions`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';
import { SUBJECTS } from '@jeraisonne/db';
import { levelLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { supabase } from '@/lib/supabase';
import { isAdmin, useAuth } from '@/lib/auth';

/** Tables et RPC postérieures aux types générés : client non typé. */
const db = supabase as unknown as SupabaseClient;

export const SPEAKER_PERMISSION = 'speaker';
export const SPEAKER_PAGE = '/audios-animations';

export const speakerKeys = {
  mine: ['user-permissions', 'mine'] as const,
  tasks: ['speaker', 'tasks'] as const,
  animation: (id: string) => ['speaker', 'animation', id] as const,
  of: (userId: string) => ['user-permissions', userId] as const,
};

// ------------------------------------------------------------ permission

/** Le compte connecté a la permission « speaker ». */
export function useIsSpeaker(): boolean {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: speakerKeys.mine,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await db
        .from('user_permissions')
        .select('permission')
        .eq('user_id', user!.id)
        .eq('permission', SPEAKER_PERMISSION)
        .maybeSingle();
      if (error) return false;
      return !!data;
    },
  });
  return q.data === true;
}

/** Accès à la rubrique : « speaker », ou admin. */
export function useCanRecordVoices(): boolean {
  const { profile } = useAuth();
  const speaker = useIsSpeaker();
  return speaker || isAdmin(profile);
}

// ------------------------------------------------------------ liste

export const speakerTaskSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  slug: z.string().nullable(),
  status: z.string(),
  subject: z.string().nullable(),
  level: z.string().nullable(),
  level_rank: z.number(),
  total: z.number().int(),
  missing: z.number().int(),
});
export type SpeakerTask = z.infer<typeof speakerTaskSchema>;

export interface SpeakerLevelGroup {
  level: string | null;
  label: string;
  missing: number;
  animations: SpeakerTask[];
}
export interface SpeakerSubjectGroup {
  subject: string | null;
  label: string;
  missing: number;
  levels: SpeakerLevelGroup[];
}

/**
 * Animations à enregistrer, par matière (ordre des matières de l'app) puis
 * niveau (6e → prépa), puis titre. PUR.
 */
export function groupSpeakerTasks(tasks: readonly SpeakerTask[]): SpeakerSubjectGroup[] {
  const subjectOrder = (s: string | null) => {
    const i = SUBJECTS.findIndex((x) => x.value === s);
    return i < 0 ? 999 : i;
  };
  const bySubject = new Map<string, SpeakerTask[]>();
  for (const t of tasks) {
    const k = t.subject ?? '';
    bySubject.set(k, [...(bySubject.get(k) ?? []), t]);
  }
  return [...bySubject.entries()]
    .sort(([a], [b]) => subjectOrder(a || null) - subjectOrder(b || null) || a.localeCompare(b))
    .map(([subject, list]) => {
      const byLevel = new Map<string, SpeakerTask[]>();
      for (const t of list) {
        const k = t.level ?? '';
        byLevel.set(k, [...(byLevel.get(k) ?? []), t]);
      }
      const levels = [...byLevel.entries()]
        .map(([level, items]) => ({
          level: level || null,
          label: level ? levelLabel(level) : 'Sans niveau',
          missing: items.reduce((n, t) => n + t.missing, 0),
          rank: items[0]!.level_rank,
          animations: [...items].sort((a, b) => a.title.localeCompare(b.title, 'fr')),
        }))
        .sort((a, b) => a.rank - b.rank)
        .map(({ rank: _rank, ...g }) => g);
      return {
        subject: subject || null,
        label: subject ? subjectLabel(subject) : 'Sans matière',
        missing: list.reduce((n, t) => n + t.missing, 0),
        levels,
      };
    });
}

export function useSpeakerTasks(enabled = true) {
  return useQuery({
    queryKey: speakerKeys.tasks,
    enabled,
    queryFn: async (): Promise<SpeakerTask[]> => {
      const { data, error } = await db.rpc('get_speaker_voice_tasks');
      if (error) throw new Error('La liste n’a pas pu être chargée.');
      return z.array(speakerTaskSchema).parse(data ?? []);
    },
  });
}

// ------------------------------------------------------------ prêtes à diffuser (admin)

export const readyToShareSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  slug: z.string().nullable(),
  status: z.string(),
  subject: z.string().nullable(),
  level: z.string().nullable(),
  level_rank: z.number(),
  /** brouillon (avec les voix) plus récent que la publication dans l'app */
  needs_republish: z.boolean(),
  /** dernière vidéo exportée (`youtube_videos.status`) ; null : jamais exportée */
  video_status: z.string().nullable(),
});
export type ReadyToShare = z.infer<typeof readyToShareSchema>;

/**
 * Admin : animations dont toutes les voix sont enregistrées mais pas encore
 * diffusées sur les réseaux (RPC `get_animations_ready_to_share`, 0684).
 */
export function useAnimationsReadyToShare(enabled: boolean) {
  return useQuery({
    queryKey: ['speaker', 'ready-to-share'] as const,
    enabled,
    queryFn: async (): Promise<ReadyToShare[]> => {
      const { data, error } = await db.rpc('get_animations_ready_to_share');
      if (error) throw new Error('La liste des animations prêtes n’a pas pu être chargée.');
      return z.array(readyToShareSchema).parse(data ?? []);
    },
  });
}

/** « Vidéo à exporter », « Aperçu prêt »… (dernière vidéo d'une animation). PUR. */
export function videoStatusLabel(status: string | null): string {
  switch (status) {
    case null:
      return 'Vidéo à exporter';
    case 'draft':
      return 'Aperçu prêt';
    case 'queued':
    case 'rendering':
    case 'encoding':
    case 'uploading':
      return 'Vidéo en cours';
    case 'failed':
      return 'Vidéo en échec';
    default:
      return 'Vidéo exportée';
  }
}

// ------------------------------------------------------------ une animation

export const speakerSlotSchema = z.object({
  key: z.string(),
  label: z.string(),
  hora: z.string().nullable(),
  text: z.string(),
  say: z.string(),
  recorded: z.boolean(),
  videoOnly: z.boolean(),
  interactive: z.boolean(),
  /** rôle à tenir (dialogue : Speaker 1, 2, 3) */
  role: z.string().default('s1'),
  roleLabel: z.string().default('Narrateur'),
  /** prénom de qui a enregistré la voix actuelle */
  recordedBy: z.string().nullable().default(null),
  /** la voix actuelle est la tienne */
  mine: z.boolean().default(false),
});
export type SpeakerSlot = z.infer<typeof speakerSlotSchema>;

const speakerAnimationSchema = z.object({
  ok: z.literal(true),
  id: z.string().uuid(),
  title: z.string(),
  slug: z.string().nullable(),
  doc: z.record(z.unknown()),
  slots: z.array(speakerSlotSchema),
});
export type SpeakerAnimation = z.infer<typeof speakerAnimationSchema>;

export const SPEAKER_ERROR_MESSAGES: Record<string, string> = {
  forbidden: 'Ton compte n’a pas (ou plus) l’accès aux voix des animations.',
  not_found: 'Animation introuvable.',
  invalid_document: 'Cette animation est en cours de modification : réessaie plus tard.',
  slot_not_found: 'Ce texte n’existe plus dans l’animation : recharge la page.',
  text_changed: 'Le texte vient de changer : recharge la page, puis enregistre le nouveau texte.',
  invalid_path: 'Prise introuvable : recommence.',
  busy: 'L’animation est modifiée en ce moment : réessaie dans un instant.',
  save_failed: 'La prise n’a pas pu être gardée : réessaie.',
};

async function speakerCall(body: Record<string, unknown>): Promise<unknown> {
  const { data, error } = await supabase.functions.invoke('animation-speaker', { body });
  if (error) {
    let code = 'unknown';
    const ctx = (error as Error & { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as { error?: unknown };
        if (typeof parsed.error === 'string') code = parsed.error;
      } catch {
        /* message générique */
      }
    }
    throw new Error(SPEAKER_ERROR_MESSAGES[code] ?? 'Erreur : réessaie dans un instant.');
  }
  return data;
}

export function useSpeakerAnimation(id: string | null) {
  return useQuery({
    queryKey: speakerKeys.animation(id ?? ''),
    enabled: !!id,
    queryFn: async () =>
      speakerAnimationSchema.parse(await speakerCall({ action: 'animation', animation_id: id })),
  });
}

/** Garde une prise : posée dans le brouillon au nom du compte. */
export function useKeepSpeakerVoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      animationId: string;
      slotKey: string;
      say: string;
      path: string;
      durationMs: number;
      words: { w: string; startMs: number; endMs: number }[];
    }) => {
      const data = z
        .object({
          ok: z.literal(true),
          slots: z.array(speakerSlotSchema),
          doc: z.record(z.unknown()),
        })
        .parse(
          await speakerCall({
            action: 'keep',
            animation_id: input.animationId,
            slot_key: input.slotKey,
            say: input.say,
            path: input.path,
            duration_ms: input.durationMs,
            words: input.words,
          }),
        );
      return { animationId: input.animationId, ...data };
    },
    onSuccess: (r) => {
      // la liste des textes et le brouillon à tester suivent tout de suite
      qc.setQueryData<SpeakerAnimation>(speakerKeys.animation(r.animationId), (old) =>
        old ? { ...old, slots: r.slots, doc: r.doc } : old,
      );
      void qc.invalidateQueries({ queryKey: speakerKeys.tasks });
      void qc.invalidateQueries({ queryKey: ['speaker', 'ready-to-share'] });
    },
  });
}

// ------------------------------------------------------------ admin

export function useUserSpeakerPermission(userId: string | null) {
  return useQuery({
    queryKey: speakerKeys.of(userId ?? ''),
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await db
        .from('user_permissions')
        .select('permission, granted_at')
        .eq('user_id', userId!)
        .eq('permission', SPEAKER_PERMISSION)
        .maybeSingle();
      if (error) throw new Error('Permission illisible.');
      return (data as { granted_at: string } | null) ?? null;
    },
  });
}

export function useSetSpeakerPermission() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { userId: string; granted: boolean }) => {
      const q = input.granted
        ? db.from('user_permissions').upsert(
            {
              user_id: input.userId,
              permission: SPEAKER_PERMISSION,
              granted_by: user?.id ?? null,
            },
            { onConflict: 'user_id,permission', ignoreDuplicates: true },
          )
        : db
            .from('user_permissions')
            .delete()
            .eq('user_id', input.userId)
            .eq('permission', SPEAKER_PERMISSION);
      const { error } = await q;
      if (error) throw new Error('La permission n’a pas pu être changée.');
    },
    onSettled: (_d, _e, input) => {
      void qc.invalidateQueries({ queryKey: speakerKeys.of(input.userId) });
      void qc.invalidateQueries({ queryKey: speakerKeys.mine });
    },
  });
}

// ------------------------------------------------------------ rôles

export interface SpeakerRoleOption {
  role: string;
  label: string;
  /** textes encore sans voix pour ce rôle */
  todo: number;
}

/** « Speaker 2 · L'élève » */
export function speakerRoleTitle(role: string, label: string): string {
  const n = /^s([1-3])$/.exec(role)?.[1];
  const speaker = n ? `Speaker ${n}` : role;
  return label && label !== speaker ? `${speaker} · ${label}` : speaker;
}

/**
 * Rôles d'une animation (dialogue : plusieurs), dans l'ordre, avec le nombre
 * de textes encore sans voix. Un seul rôle : monologue. PUR.
 */
export function speakerRoleOptions(slots: readonly SpeakerSlot[]): SpeakerRoleOption[] {
  const out: SpeakerRoleOption[] = [];
  for (const s of slots) {
    let o = out.find((x) => x.role === s.role);
    if (!o) {
      o = { role: s.role, label: s.roleLabel, todo: 0 };
      out.push(o);
    }
    if (!s.recorded) o.todo++;
  }
  return out.sort((a, b) => a.role.localeCompare(b.role));
}
